import {
  createBaseThemeOptions,
  pageTheme as defaultPageThemes,
  PageTheme,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';

import { alpha } from '@mui/material/styles';

export const SIDEBAR_ITEM_LABEL_FONT_SIZE = 13;

const pageThemesFontColorOverride: Record<string, PageTheme> = {};
Object.keys(defaultPageThemes).forEach(key => {
  pageThemesFontColorOverride[key] = {
    ...defaultPageThemes[key],
    fontColor: '#172B4D',
  };
});

export const makeTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: '#6d00cc',
        light: '#3da5da',
        dark: '#172B4D',
      },
      secondary: {
        main: '#4000cc',
        light: '#fecd5f',
        dark: '#6554C0',
      },
      navigation: {
        ...palettes.light.navigation,
        background: 'linear-gradient(180deg, #b55dcd 0, #724ebf 100%)',
        color: '#FFFFFF',
        indicator: '#FFFFFF',
        navItem: {
          hoverBackground: 'rgba(0, 0, 0, .2)',
        },
        submenu: {
          background: '#1c1e26',
        },
      },
      text: {
        primary: '#333333',
      },
      link: '#6d00cc',
      linkHover: '#440080',
      banner: {
        ...palettes.dark.banner,
        info: '#3da5da',
      },
    },
  }),
  pageTheme: pageThemesFontColorOverride,
  defaultPageTheme: 'home',
  components: {
    BackstageSidebarItem: {
      styleOverrides: {
        label: {
          fontSize: SIDEBAR_ITEM_LABEL_FONT_SIZE,
        },
      },
    },
    BackstageHeader: {
      styleOverrides: {
        header: ({ theme }) => ({
          backgroundImage: 'unset',
          boxShadow: '1px 1px 1px #E3E3E3',
          backgroundColor: '#FFFFFF',
          padding: theme.spacing(3),
        }),
        title: ({ theme }) => ({
          color: theme.page.fontColor,
          fontWeight: 900,
          fontSize: '1.5rem',
        }),
        subtitle: {
          display: 'none',
        },
        type: ({ theme }) => ({
          color: alpha(theme.page.fontColor, 0.8),
        }),
      },
    },
    BackstageContent: {
      styleOverrides: {
        root: () => ({
          padding: 8,
        }),
      },
    },
    BackstageContentHeader: {
      styleOverrides: {
        container: {
          marginBottom: 0,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        defaultTab: {
          fontSize: 'inherit',
          textTransform: 'none',
          padding: '10px',
        },
      },
    },
    BackstageOpenedDropdown: {
      styleOverrides: {
        icon: {
          '& path': {
            fill: '#FFFFFF',
          },
        },
      },
    },
    BackstageItemCardHeader: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'unset !important',
          backgroundColor: theme.palette.primary.main,
          color: `${theme.palette.primary.contrastText} !important`,
        }),
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(9,30,69,0.54)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        ...palettes.light,
        root: ({ theme }) => ({
          '& button': {
            color: theme.palette.link,
            '& span': {
              padding: '5px',
              fontSize: '1rem',
            },
            '& span:hover': {
              color: theme.palette.linkHover,
            },
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        h5: {
          marginBottom: 0,
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        container: {
          padding: 8,
        },
        item: {
          paddingTop: 8,
          paddingLeft: 8,
          paddingRight: 8,
          paddingBottom: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          marginBottom: 10,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          margin: 4,
        },
        outlined: {
          margin: 4,
        },
        sizeSmall: {
          margin: 4,
        },
      },
    },

    MuiLink: {
      styleOverrides: {
        root: {
          color: '#6d00cc',
        },
        underlineHover: {
          '&:hover': {
            color: '#440080',
            textDecoration: 'none',
          },
        },
      },
    },

    MuiButton: {
      styleOverrides: {
        containedPrimary: {
          color: '#FFF !important',
          marginBottom: 16,
        },
      },
    },
  },
});

/**
 * Upstream dark theme on Make's dark surfaces (Domino inverted-warm-grey),
 * matching the BUI dark tokens in bui.css so legacy MUI pages and BUI pages
 * share one background.
 */
export const makeDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    background: {
      default: '#1c1b1d',
      paper: '#262526',
    },
    navigation: {
      ...palettes.dark.navigation,
      background: '#262526',
      submenu: {
        ...palettes.dark.navigation.submenu,
        background: '#2d2c2e',
      },
    },
  },
});
